import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { catalogService } from '../../shared/services';
import { PlayCircle, CheckCircle, Circle, Clock, Award } from 'lucide-react';

export function CoursePage() {
  const { slug, id } = useParams<{ slug?: string; id?: string }>();

  const { data: curso, isLoading, isError } = useQuery({
    queryKey: ['curso', slug || id],
    queryFn: () => {
      if (id) {
        return catalogService.getCurso(id);
      }
      return catalogService.getCursoBySlug(slug!);
    },
    enabled: !!(slug || id),
    retry: false, // Não tentar novamente em caso de 404
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isError || !curso) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md px-6">
          <div className="mb-6">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Curso ainda não disponível</h2>
          <p className="text-gray-600 mb-6">
            Este curso faz parte da trilha, mas ainda está sendo preparado. Em breve estará disponível para você!
          </p>
          <div className="flex gap-3 justify-center">
            <Link 
              to="/trilhas" 
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              Ver trilhas
            </Link>
            <Link 
              to="/catalogo" 
              className="px-6 py-2.5 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-lg transition-colors"
            >
              Ver catálogo
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Achatar todas as aulas de todos os módulos em uma lista única
  const todasAulas = curso.modulos?.flatMap((modulo, modIndex) => 
    (modulo.aulas || []).map((aula, aulaIndex) => ({
      ...aula,
      moduloTitulo: modulo.titulo,
      numero: `${modIndex + 1}.${aulaIndex + 1}`,
      moduloIndex: modIndex,
      aulaIndex: aulaIndex
    }))
  ) || [];

  const primeiraAulaNaoConcluida = todasAulas.find(
    aula => aula.progresso?.status !== 'concluida'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero compacto */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-medium uppercase tracking-wide">
              {curso.categoria || 'ERP'}
            </span>
          </div>
          
          <h1 className="text-3xl font-bold mb-3">{curso.titulo}</h1>
          <p className="text-base text-primary-100 mb-4 max-w-4xl">{curso.descricao}</p>
          
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Clock size={18} />
              <span>{curso.cargaHorariaMin || 0} minutos</span>
            </div>
            <div className="flex items-center gap-2">
              <PlayCircle size={18} />
              <span>{todasAulas.length} aulas</span>
            </div>
            {curso.progresso && curso.progresso.percentual > 0 && (
              <div className="flex items-center gap-2">
                <Award size={18} />
                <span>{curso.progresso.percentual}% concluído</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Layout principal - estilo FM2S */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Conteúdo principal - 70% */}
          <div className="lg:col-span-8">
            {todasAulas.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Header do conteúdo */}
                <div className="border-b border-gray-200 px-6 py-4">
                  <h2 className="text-xl font-bold text-gray-900">Conteúdo do Curso</h2>
                </div>

                {/* Lista de módulos e aulas - estilo FM2S */}
                <div className="divide-y divide-gray-100">
                  {curso.modulos?.map((modulo, modIndex) => (
                    <div key={modulo.id} className="px-6 py-4">
                      {/* Título do módulo */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-primary-700">{modIndex + 1}</span>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">
                          {modulo.titulo}
                        </h3>
                      </div>

                      {/* Lista de aulas do módulo */}
                      <div className="ml-11 space-y-2">
                        {modulo.aulas?.map((aula) => {
                          const isCompleted = aula.progresso?.status === 'concluida';
                          
                          return (
                            <Link
                              key={aula.id}
                              to={`/aulas/${aula.id}`}
                              className="flex items-center gap-3 py-2 px-3 -mx-3 hover:bg-gray-50 rounded-lg transition-all group"
                            >
                              {/* Ícone de status */}
                              <div className="flex-shrink-0">
                                {isCompleted ? (
                                  <CheckCircle size={20} className="text-green-500" />
                                ) : (
                                  <Circle size={20} className="text-gray-300 group-hover:text-primary-400 transition-colors" />
                                )}
                              </div>
                              
                              {/* Título da aula */}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium transition-colors ${
                                  isCompleted 
                                    ? 'text-gray-500 line-through' 
                                    : 'text-gray-700 group-hover:text-primary-600'
                                }`}>
                                  {aula.titulo}
                                </p>
                              </div>
                              
                              {/* Duração */}
                              {aula.duracaoSeg && (
                                <span className="text-xs text-gray-400 flex-shrink-0 font-medium">
                                  {Math.floor(aula.duracaoSeg / 60)} min
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Nenhum conteúdo disponível */
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <PlayCircle size={48} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">Nenhuma aula disponível ainda.</p>
                <p className="text-gray-400 text-sm mt-1">As aulas serão adicionadas em breve.</p>
              </div>
            )}
          </div>

          {/* Sidebar direita - 30% - estilo FM2S */}
          <div className="lg:col-span-4">
            <div className="sticky top-8 space-y-4">
              {/* Card de ação */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Comece Agora
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Inicie este curso e comece sua jornada de aprendizado.
                </p>
                
                {primeiraAulaNaoConcluida ? (
                  <Link
                    to={`/aulas/${primeiraAulaNaoConcluida.id}`}
                    className="btn-primary w-full block text-center"
                  >
                    Iniciar Curso
                  </Link>
                ) : todasAulas.length > 0 ? (
                  <Link
                    to={`/aulas/${todasAulas[0].id}`}
                    className="btn-primary w-full block text-center"
                  >
                    Revisar Curso
                  </Link>
                ) : (
                  <button className="btn-primary w-full opacity-50 cursor-not-allowed" disabled>
                    Aguardando Conteúdo
                  </button>
                )}

                {/* Informações adicionais */}
                <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Nível</span>
                    <span className="font-medium text-gray-900">{curso.nivel || 'Iniciante'}</span>
                  </div>
                  
                  {curso.instrutor && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Instrutor</span>
                      <span className="font-medium text-gray-900">{curso.instrutor}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Total de aulas</span>
                    <span className="font-medium text-gray-900">{todasAulas.length}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Duração</span>
                    <span className="font-medium text-gray-900">{curso.cargaHorariaMin || 0} min</span>
                  </div>
                </div>
              </div>

              {/* Card de progresso (se houver) */}
              {curso.progresso && curso.progresso.percentual > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Seu Progresso
                  </h3>
                  
                  {/* Barra de progresso */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Concluído</span>
                      <span className="font-bold text-primary-600">{curso.progresso.percentual}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-500"
                        style={{ width: `${curso.progresso.percentual}%` }}
                      />
                    </div>
                  </div>

                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {todasAulas.filter(a => a.progresso?.status === 'concluida').length}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Aulas completas</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {todasAulas.length - todasAulas.filter(a => a.progresso?.status === 'concluida').length}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Restantes</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
